const API_URL = process.env.REACT_APP_API_URL || '/api';

export type MessageBoardPostUrgency = 'NORMAL' | 'URGENT';

export interface CreateMessageBoardPostPayload {
  message: string;
  foremanAreaId: number;
  urgency: MessageBoardPostUrgency;
  /** When set, the post goes only to these people in foremanAreaId. */
  recipientUserIds?: number[];
}

export interface MessageBoardServiceAreaOption {
  id: number;
  name: string;
  foremanAreaIds: number[];
}

export interface MessageBoardPersonOption {
  id: number;
  name: string;
}

export interface MessageBoardPostCreated {
  id: number;
  createdAt: string;
  message: string;
  urgency: MessageBoardPostUrgency;
  foremanAreaId: number;
  createdByUserId: number;
  deletedAt: string | null;
  deletedByUserId: number | null;
  recipientCount: number;
  readCount: number;
}

export interface MessageBoardPostListItem {
  id: number;
  message: string;
  urgency: MessageBoardPostUrgency;
  createdAt: string;
  createdBy: { id: number; name: string };
  foremanArea: { id: number; name: string };
  readCount: number;
  recipientCount: number;
}

export interface ListMessageBoardPostsParams {
  foremanAreaId: number;
  urgency?: MessageBoardPostUrgency;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface MessageBoardPostListResult {
  data: MessageBoardPostListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
  recentCount: number;
}

export interface MessageBoardPostRemoved {
  id: number;
  deletedAt: string;
  deletedByUserId: number;
}

export interface MessageBoardPostSeenReceipt {
  name: string;
  seenAt: string;
}

export interface MessageBoardPostNotSeen {
  name: string;
}

export interface MessageBoardPostReads {
  postId: number;
  urgency: MessageBoardPostUrgency;
  recipientCount: number;
  seenCount: number;
  seen: MessageBoardPostSeenReceipt[];
  notSeen: MessageBoardPostNotSeen[];
}

export interface MessageBoardPostReadAudit {
  id: number;
  seenAt: string;
  user: { id: number; name: string };
}

export interface MessageBoardPostAudit {
  id: number;
  message: string;
  urgency: MessageBoardPostUrgency;
  createdAt: string;
  deletedAt: string | null;
  createdBy: { id: number; name: string };
  deletedBy: { id: number; name: string } | null;
  foremanArea: { id: number; name: string };
  reads: MessageBoardPostReadAudit[];
}

async function throwFromResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    details?: string;
  };
  const msg = body?.error || fallback;
  throw new Error(body?.details ? `${msg}: ${body.details}` : msg);
}

const messageBoardService = {
  create: async (data: CreateMessageBoardPostPayload): Promise<MessageBoardPostCreated> => {
    const response = await fetch(`${API_URL}/message-board`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to create message board post');
    }
    return response.json();
  },

  listAudience: async (): Promise<{ serviceAreas: MessageBoardServiceAreaOption[] }> => {
    const response = await fetch(`${API_URL}/message-board/audience`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to load service areas');
    }
    return response.json();
  },

  listPeople: async (foremanAreaId: number): Promise<{ people: MessageBoardPersonOption[] }> => {
    const qs = new URLSearchParams({ foremanAreaId: String(foremanAreaId) });
    const response = await fetch(`${API_URL}/message-board/people?${qs.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to load people');
    }
    return response.json();
  },

  list: async (params: ListMessageBoardPostsParams): Promise<MessageBoardPostListResult> => {
    const qs = new URLSearchParams();
    qs.set('foremanAreaId', String(params.foremanAreaId));
    if (params.urgency) qs.set('urgency', params.urgency);
    if (params.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params.dateTo) qs.set('dateTo', params.dateTo);
    qs.set('page', String(params.page ?? 1));
    qs.set('pageSize', String(params.pageSize ?? 5));

    const response = await fetch(`${API_URL}/message-board?${qs.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to load message board posts');
    }
    return response.json();
  },

  getReads: async (id: number): Promise<MessageBoardPostReads> => {
    const response = await fetch(`${API_URL}/message-board/${id}/reads`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to load read receipts');
    }
    return response.json();
  },

  remove: async (id: number): Promise<MessageBoardPostRemoved> => {
    const response = await fetch(`${API_URL}/message-board/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to take down message board post');
    }
    return response.json();
  },

  listRemoved: async (foremanAreaId: number): Promise<{ data: MessageBoardPostAudit[] }> => {
    const qs = new URLSearchParams({ foremanAreaId: String(foremanAreaId) });
    const response = await fetch(`${API_URL}/message-board/audit?${qs.toString()}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await throwFromResponse(response, 'Failed to load removed messages');
    }
    return response.json();
  },
};

export default messageBoardService;
